module Meevo.Plugins.FameAdvantage {
    //#region enums
    import Employee = Meevo.Model.Employee;
    import AppliedTemplateSection = Meevo.Model.AppliedTemplateSection;

    export enum FameAdvantageStatusEnum {
        Active = 11039671,
        Inactive = 11039672,
        Terminated = 11039673,
        Deleted = 11039674
    }

    export enum FameAdvantageImportTypeEnum {
        Add,
        Update,
    }

    export enum PunchTypeEnum {
        ClockIn = 1,
        BreakInTime = 3,
        AnyOut = 5,
    }

    export enum ImportResultEnum {
        Successful,
        Updated,
        Failed,
    }

    //#endregion enums

    // region Interfaces

    export interface IImportModel {
        fameStudents: IFameStudent[];
        password?: string;
        templateId?: string;
        appliedTemplates?: IAppliedTemplate[];
    }

    export interface IFameStudent {
        studentId: string;
        studentEnrollmentId: string;
        firstName: string;
        lastName: string;
        bestEmail: string;
        bestPhone: string;
        bestAddress: string;
        city: string;
        state: string;
        zip: string;
        gender: string;
        startDate: Date;
        status: string;
        statusCodeId: string;
        sysStatusId: number;
        programName: string;
        programId: string;
        programVersionName: string;
        programVersionId: string;

        meevoStudentId?: string;
    }

    export interface IAdvantageMeevoStudent {
        gender: number;
        isBookableOnline: boolean;
        isEmployeeBioEnable: boolean;
        isDoubleBookingAllowed: boolean;
        appointmentBookOrder: number;
        isEmployee: boolean;
        allowTips: boolean;
        allowSales: boolean;
        resourceType: number;
        contentType: string;
        isStorageUpdated: boolean;
        isShared: boolean;
        allowBookableWalkIn: boolean;
        employeeEntityType: number;
        externalStudentId: string;
        firstName: string;
        lastName: string;
        code: string;
        displayName: string;
        ownedByLocationId: number;
        atLocationId: number;
        totalLocations: number;
        availableAtLocations: number;
        objectState: number;
        objectCreationType: number;
        id: string;
        locationId: number;
    }

    export interface IImportAdvantageData {
        fameStudent: IFameStudent;
        meevoStudent?: IAdvantageMeevoStudent;
        student?: Employee; // for typeahead
    }

    export interface IAppliedTemplate {
        templateId: string;
        displayName: string;
        sections: AppliedTemplateSection[];
    }

    export interface IImportRes {
        addedMeevoStudents: Employee[];
        updatedMeevoStudents: Employee[];
        errors: IImportError[];
    }

    export interface IImportError {
        fameStudent: IFameStudent;
        errorMessage: string;
    }

    export interface IAdvantageSettings {
        userName: string;
        password: string;
        advantageAPIURL: string;
        currentlyAttending: FameAdvantageStatusEnum;
        futureStart: FameAdvantageStatusEnum;
        noStart: FameAdvantageStatusEnum;
        leaveOfAbsence: FameAdvantageStatusEnum;
        suspension: FameAdvantageStatusEnum;
        dropped: FameAdvantageStatusEnum;
        graduated: FameAdvantageStatusEnum;
        transferOut: FameAdvantageStatusEnum;
        externship: FameAdvantageStatusEnum;
        tenantName: string;
    }

    export interface IAdvantageTestConnectRes {
        success: boolean;
    }

    export interface IAdvantageTestConnectModel {
        username: string;
        password: string;
        advantageAPIURL: string;
        tenantName: string;
    }

    export interface IWorkTypeDetail {
        id: string;
        displayName: string;
    }

    export interface IClock extends IStartEndDateTime {
        breaks: IBreak[];
        autoClock: boolean;
        clockDate: Date;
        employeeId: string;
        workTypeDisplayName: string;
        workTypeId: string;
        workTypeDetail: IWorkTypeDetail;
        clockTime_LId: string;
        locationId: number;
    }

    export interface IBreak extends IStartEndDateTime {
        breakTimeId: string;
        clockTime_LId: string;
        locationId: number;
    }

    export interface IStartEndDateTime {
        startDateTime: Date;
        endDateTime?: Date;
    }

    export interface ITimeClockDay {
        clocks: IClock[];
        firstName: string;
        lastName: string;
        employeeCode: string;
        employeeId: string;
        clockDate: Date;
        autoClock: boolean;
        totalBreaks: number;
        invalidClock: boolean;
        missingPunchIn: boolean;
        addClockInOnly: boolean;
    }

    export interface ITimeClockEmployee {
        totalActuals: string;
        employeeId: string;
        firstName: string;
        lastName: string;
        employeeCode: string;
        badgeNumber?: string;
        externalStudentId: string;
        days: ITimeClockDay[];
    }

    export interface IImportResult {
        studentId: string;
        firstName: string;
        lastName: string;
        status: ImportResultEnum;
        message: string;
    }

    export interface ITimeClockExportModel {
        studentId: string;
        displayName: string;
        punchDate: Date;
        timeIn: Date;
        timeOut?: Date;

        scheduleData(): { employee: ITimeClockEmployee; day: ITimeClockDay; };

        formatForFile(): string;
    }

    abstract class TimeClockExportModel<T extends IStartEndDateTime> implements ITimeClockExportModel {

        private readonly student: ITimeClockEmployee;
        private readonly day: ITimeClockDay;
        private readonly clockDay: T;

        public get studentId() {
            return this.student.externalStudentId;
        }

        public get displayName() {
            return `${this.student.employeeCode}: ${this.student.lastName}, ${this.student.firstName}`;
        }

        public get punchDate() {
            return this.day.clockDate;
        }

        public get timeIn() {
            return this.clockDay.startDateTime;
        }

        public get timeOut() {
            return this.clockDay.endDateTime;
        }

        public abstract get punchInType(): PunchTypeEnum;

        public abstract get punchOutType(): PunchTypeEnum;

        private get recordType(): string {
            return 'PN00';
        }

        private get badgeNumberForFile(): string {
            const MAX_BADGE_NUMBER_LENGTH = 5;
            let badgeNumber = !!this.student.badgeNumber ? this.student.badgeNumber.slice(0, MAX_BADGE_NUMBER_LENGTH) : '';

            while (badgeNumber.length < MAX_BADGE_NUMBER_LENGTH) {
                badgeNumber = '0' + badgeNumber;
            }

            return badgeNumber;
        }

        private get typeOfEntry(): string {
            return 'K';
        }

        constructor(student: ITimeClockEmployee, day: ITimeClockDay, clockDay: T) {
            this.student = student;
            this.day = day;
            this.clockDay = clockDay;
        }

        public scheduleData(): { employee: ITimeClockEmployee; day: ITimeClockDay } {
            return {
                employee: this.student,
                day: this.day
            };
        }

        public formatForFile(): string {
            return `${this.punchRecordForFile(this.punchInType)}\n${this.punchRecordForFile(this.punchOutType)}`;
        }

        private punchRecordForFile(punchType: PunchTypeEnum): string {
            const punchDate = moment(this.punchDate).format('YYYYMMDD');
            let time: string = '';
            switch (punchType) {
                case PunchTypeEnum.AnyOut:
                    time = moment(this.timeOut).format('HHmmSS');
                    break;
                case PunchTypeEnum.BreakInTime:
                case PunchTypeEnum.ClockIn:
                    time = moment(this.timeIn).format('HHmmSS');
                    break;
            }

            return `${this.recordType} ${punchDate}${time} ${punchType}0000${this.typeOfEntry}0000${this.badgeNumberForFile} `;
        }

    }

    export class DayTimeClockExportModel extends TimeClockExportModel<IClock> implements ITimeClockExportModel {
        public get punchInType(): PunchTypeEnum {
            return PunchTypeEnum.ClockIn;
        }

        public get punchOutType(): PunchTypeEnum {
            return PunchTypeEnum.AnyOut;
        }
    }

    export class BreakTimeClockExportModel extends TimeClockExportModel<IBreak> implements ITimeClockExportModel {
        public get punchInType(): PunchTypeEnum {
            return PunchTypeEnum.BreakInTime;
        }

        public get punchOutType(): PunchTypeEnum {
            return PunchTypeEnum.AnyOut;
        }
    }

    // endregion Interfaces
}
